import { Component, EventEmitter, Input, OnChanges, Output, SimpleChanges } from '@angular/core';

interface FilterRow {
  name: string;
  path: string;
  type: string;
  valueText: string;
}

/**
 * Editor for report-template filters.
 *
 * Report templates store filters as a TYPED ARRAY — `[{ name?, path, type, value }]` —
 * which the API's buildFilters() consumes by switching on `type`. This is a different
 * model from view filters (a Mongo-style keyed object handled by FilterEditorComponent),
 * so the two must not be conflated: emitting an object here silently breaks report
 * filtering because the API guards on `report.filters.length`.
 */
@Component({
  selector: 'app-report-filter-editor',
  templateUrl: './report-filter-editor.component.html',
  styleUrls: ['./report-filter-editor.component.scss']
})
export class ReportFilterEditorComponent implements OnChanges {

  @Input() filters: any;
  @Output() filtersChange = new EventEmitter<any>();

  // Mirrors the cases handled by buildFilters() on the API.
  types = [
    { key: 'string', name: 'string' },
    { key: 'cardNumber', name: 'card number' },
    { key: 'boolean', name: 'boolean' },
    { key: 'number', name: 'number (min/max)' },
    { key: 'currency', name: 'currency (min/max)' },
    { key: 'currencyInverse', name: 'currency inverse' },
    { key: 'currencyDetail', name: 'currency detail' },
    { key: 'dateTime', name: 'date/time' },
    { key: 'notNull', name: 'is not null' },
    { key: 'id', name: 'id' },
    { key: 'idOmit', name: 'id omit' },
    { key: 'idExclusive', name: 'id exclusive' },
    { key: 'activeUserId', name: 'active user id' },
    { key: 'remoteId', name: 'remote id' },
    { key: 'sourceCollection', name: 'source collection' },
    { key: 'or', name: 'or (JSON)' }
  ];

  // Types whose value is ignored by the API.
  valuelessTypes = ['notNull', 'activeUserId'];

  rows: FilterRow[] = [];

  private lastEmitted: any;

  ngOnChanges(changes: SimpleChanges): void {
    // Only re-parse when the change came from outside, not from our own emit
    if (changes.filters && changes.filters.currentValue !== this.lastEmitted) {
      this.parseFilters();
    }
  }

  parseFilters() {
    this.rows = [];

    // Accept the canonical array form, and self-heal the legacy/corrupted
    // numeric-keyed object form ({ "0": {...}, "1": {...} }) by reading its values.
    const entries = this.toEntryArray(this.filters);

    for (const entry of entries) {
      if (!entry || typeof entry !== 'object') {
        continue;
      }
      this.rows.push({
        name: entry.name || '',
        path: entry.path || '',
        type: entry.type || 'string',
        valueText: this.stringifyValue(entry.value)
      });
    }
  }

  toEntryArray(filters: any): any[] {
    if (Array.isArray(filters)) {
      return filters;
    }
    if (filters && typeof filters === 'object') {
      return Object.keys(filters)
        .sort((a, b) => Number(a) - Number(b))
        .map(key => filters[key]);
    }
    return [];
  }

  hasValue(type: string): boolean {
    return this.valuelessTypes.indexOf(type) === -1;
  }

  stringifyValue(value: any): string {
    if (value === undefined) {
      return '';
    }
    if (value === null) {
      return 'null';
    }
    if (typeof value === 'string') {
      return value;
    }
    if (typeof value === 'object') {
      return JSON.stringify(value);
    }
    return String(value);
  }

  parseValue(text: string, type: string): any {
    if (!this.hasValue(type)) {
      return undefined;
    }

    const trimmed = (text || '').trim();
    if (trimmed === '') {
      return undefined;
    }

    // Structured values (arrays, objects) are entered as JSON.
    if (trimmed[0] === '{' || trimmed[0] === '[') {
      try {
        return JSON.parse(trimmed);
      } catch (err) {
        return text;
      }
    }

    if (trimmed === 'true') {
      return true;
    }
    if (trimmed === 'false') {
      return false;
    }
    if (trimmed === 'null') {
      return null;
    }

    // Coerce to number only for the numeric/date types; keep ids, card numbers,
    // and strings (which may be numeric-looking) as strings.
    const numericTypes = ['number', 'currency', 'currencyInverse', 'currencyDetail', 'dateTime'];
    if (numericTypes.indexOf(type) !== -1 && !isNaN(Number(trimmed))) {
      return Number(trimmed);
    }

    return text;
  }

  addRow() {
    this.rows.push({ name: '', path: '', type: 'string', valueText: '' });
  }

  removeRow(index: number) {
    this.rows.splice(index, 1);
    this.emitFilters();
  }

  emitFilters() {
    const filters: any[] = [];

    for (const row of this.rows) {
      if (!row.path) {
        continue;
      }

      const filter: any = {
        path: row.path,
        type: row.type
      };

      if (row.name) {
        filter.name = row.name;
      }

      const value = this.parseValue(row.valueText, row.type);
      if (value !== undefined) {
        filter.value = value;
      }

      filters.push(filter);
    }

    this.filters = filters;
    this.lastEmitted = filters;
    this.filtersChange.emit(filters);
  }
}
